import React from 'react';
import { AutoSizer, Table, Column } from 'react-virtualized';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Empty, Spin, Tag, Tooltip } from 'antd';
import { EditOutlined, FilterFilled, FilterOutlined } from '@ant-design/icons';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { PartTypeAttribute } from '../../../../../types/attributes';
import { StandardResource, StandardResourceDescription } from '../../../../../types/resources';
import TableFilterPopover from '../../../global/tableFilters/TableFilterPopover';
import AttributeNamePopover from '../../attributes/AttributeNamePopover';
import { AtrributeSourceIds } from '../../../../constants/AttributeConstants';
import { DragHandle } from '../../../global/dragableTable/DragHandle';
import { DragRow } from '../../../global/dragableTable/DragRow';

type AttributeManagingTableProps = {
  fetchingAttributes: boolean;
  disabled: boolean;
  attributes: PartTypeAttribute[];
  selectedAttributeIds: number[];
  sources: StandardResourceDescription[];
  attributeFilterValues: StandardResource[];
  selectAttribute: (id: number, customAttribute: boolean) => void;
  hideAttribute: (id: number) => void;
  showAttribute: (id: number) => void;
  deleteAttribute: (id: number) => void;
  handleSort: (oldIndex: number, newIndex: number) => void;
  editAtrributeName: (attrName: string, rowIndex: number, id: number) => void;
  filterAttributeIds: number[];
  filterSourceIds: number[];
  handleFilterAttributes: (ids: number[]) => void;
  handleFilterSource: (ids: number[]) => void;
  handleSelectAll: () => void;
};

const AttributeManagingTable: React.FC<AttributeManagingTableProps> = ({
  fetchingAttributes,
  disabled,
  attributes,
  selectedAttributeIds,
  sources,
  selectAttribute,
  hideAttribute,
  showAttribute,
  deleteAttribute,
  handleSort,
  editAtrributeName,
  filterAttributeIds,
  filterSourceIds,
  handleFilterAttributes,
  handleFilterSource,
  attributeFilterValues,
  handleSelectAll,
}) => {
  const { t } = useTranslation();

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = attributes.findIndex(
        record => record.id === active?.id || record.tempId === active?.id
      );
      const overIndex = attributes.findIndex(
        record => record.id === over?.id || record.tempId === over?.id
      );
      handleSort(activeIndex, overIndex);
    }
  };

  const noData = () => {
    if (fetchingAttributes)
      return <Spin spinning className="delivery__log-top-spinner" size="large" />;

    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  };

  return (
    <AutoSizer>
      {({ height, width }) => (
        <div style={{ height, width }}>
          <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
            <SortableContext
              items={attributes.map(i => i.id || i.tempId!)}
              strategy={verticalListSortingStrategy}
            >
              <Table
                lockAxis="y"
                // @ts-ignore
                height={height}
                width={width - 2}
                headerHeight={40}
                rowCount={attributes.length}
                dataSource={attributes}
                rowHeight={40}
                rowGetter={({ index }: { index: number }) => attributes[index]}
                rowRenderer={props => {
                  const { key, ...restProps } = props;
                  return (
                    <DragRow
                      key={key}
                      {...restProps}
                      columns={props.columns}
                      rowId={attributes[props.index].id || attributes[props.index].tempId!}
                    />
                  );
                }}
                noRowsRenderer={noData}
                pressDelay={120}
              >
                <Column width={15} dataKey="index" cellRenderer={() => <DragHandle />} />
                <Column
                  label={t('attributeManaging:active')}
                  dataKey="active"
                  width={45}
                  cellRenderer={({ rowData }) => (
                    <Checkbox
                      checked={selectedAttributeIds.includes(rowData.id || rowData.tempId)}
                      onChange={() => {
                        selectAttribute(rowData.id || rowData.tempId, rowData.source_id === 7);
                      }}
                      data-testid="select-row"
                    />
                  )}
                  headerRenderer={() => (
                    <Checkbox
                      checked={
                        selectedAttributeIds.length > 0 &&
                        selectedAttributeIds.length === attributes.length
                      }
                      indeterminate={
                        selectedAttributeIds.length > 0 &&
                        selectedAttributeIds.length < attributes.length
                      }
                      onChange={() => handleSelectAll()}
                    />
                  )}
                />
                <Column
                  label={t('attributes:attribute')}
                  dataKey="name"
                  width={300}
                  flexGrow={1}
                  headerRenderer={({ label }) => (
                    <div className="flex justify-between">
                      <div>{label}</div>
                      <div className="ml-3">
                        <TableFilterPopover
                          values={attributeFilterValues}
                          handleChange={ids => handleFilterAttributes(ids)}
                          initiallySelectedIds={filterAttributeIds}
                          showSearch
                        >
                          {filterAttributeIds.length > 0 ? (
                            <FilterFilled className="table-filter-blue" />
                          ) : (
                            <FilterOutlined />
                          )}
                        </TableFilterPopover>
                      </div>
                    </div>
                  )}
                  cellRenderer={({ rowData, rowIndex }) => {
                    const itemCount = (
                      <Tooltip title={t('attributes:previouslyUsedInfo')}>
                        <Tag className="self-center item-count">{rowData.item_count}</Tag>
                      </Tooltip>
                    );
                    if (rowData.source_id === 7)
                      return (
                        <div className="flex justify-between">
                          <div>{rowData.name}</div>
                          <div className="flex">
                            <AttributeNamePopover
                              attributeName={rowData.name}
                              handleChange={attributeName =>
                                editAtrributeName(attributeName, rowIndex, rowData.id)
                              }
                              translations={rowData.translations}
                              warnText={
                                rowData.item_count && rowData.item_count > 0
                                  ? t('attributes:warnText', { count: rowData.item_count })
                                  : undefined
                              }
                              detailedConfirmWarnText={
                                rowData.item_count && rowData.item_count > 0
                                  ? t('attributes:detailedWarnText', { count: rowData.item_count })
                                  : undefined
                              }
                            >
                              <EditOutlined className="edit-button" />
                            </AttributeNamePopover>
                            {rowData.item_count > 0 && itemCount}
                          </div>
                        </div>
                      );
                    return (
                      <div className="flex justify-between">
                        <div>{rowData.name}</div>
                        {rowData.item_count > 0 && itemCount}
                      </div>
                    );
                  }}
                />
                <Column
                  label={t('attributes:source')}
                  dataKey="source_id"
                  width={200}
                  headerRenderer={({ label }) => {
                    return (
                      <div className="flex justify-between">
                        <div>{label}</div>
                        <div className="ml-3">
                          <TableFilterPopover
                            values={sources.filter(s => AtrributeSourceIds.includes(s.id))}
                            handleChange={ids => {
                              handleFilterSource(ids);
                            }}
                            initiallySelectedIds={filterSourceIds}
                            showSearch
                          >
                            {filterSourceIds.length > 0 ? (
                              <FilterFilled className="table-filter-blue" />
                            ) : (
                              <FilterOutlined />
                            )}
                          </TableFilterPopover>
                        </div>
                      </div>
                    );
                  }}
                  cellRenderer={({ rowData }) => {
                    const source = sources.find(s => s.id === rowData.source_id);
                    return <span>{source?.name}</span>;
                  }}
                />
                <Column
                  label={t('attributes:tags')}
                  dataKey="tags"
                  width={400}
                  flexGrow={1}
                  cellRenderer={({ rowData }) => (
                    <span>
                      {rowData.tags &&
                        rowData.tags.map((tag: string) => <Tag key={tag}>{tag}</Tag>)}
                    </span>
                  )}
                />
                <Column
                  label={t('common:actions')}
                  dataKey="actions"
                  width={120}
                  cellRenderer={({ rowData }) => (
                    <React.Fragment>
                      {rowData.source_id === 7 && (
                        <Tooltip title={t('attributes:deleteAttributeInfo')}>
                          <Button
                            onClick={() => deleteAttribute(rowData.id || rowData.tempId)}
                            disabled={disabled}
                            size="small"
                            danger
                          >
                            {t('common:delete')}
                          </Button>
                        </Tooltip>
                      )}

                      {!!rowData.hide && (
                        <Tooltip title={t('attributes:showAttributeInfo')}>
                          <Button
                            className="ml-1"
                            onClick={() => showAttribute(rowData.id || rowData.tempId)}
                            disabled={disabled}
                            size="small"
                          >
                            {t('common:show')}
                          </Button>
                        </Tooltip>
                      )}
                      {!rowData.hide && (
                        <Tooltip title={t('attributes:hideAttributeInfo')}>
                          <Button
                            className="ml-1"
                            onClick={() => hideAttribute(rowData.id || rowData.tempId)}
                            disabled={disabled}
                            size="small"
                          >
                            {t('common:hide')}
                          </Button>
                        </Tooltip>
                      )}
                    </React.Fragment>
                  )}
                />
              </Table>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </AutoSizer>
  );
};

export default AttributeManagingTable;
